import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Shield, Eye, Lock, Users, FileText, Mail, ExternalLink, CheckCircle, AlertCircle, Calendar, Search } from 'lucide-react';
import Banner from './Banner';

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdated] = useState(new Date('2024-01-15'));
  const [readingSections, setReadingSections] = useState(new Set());
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const markAsRead = (sectionId) => {
    setReadingSections(prev => new Set([...prev, sectionId]));
  };

  const sections = [
    {
      id: 'overview',
      title: 'Privacy Overview',
      icon: Shield,
      summary: 'How we protect and handle your personal information',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Techmentee Inc is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <a href="https://learn.techmentee.com" className="text-blue-600 hover:underline">learn.techmentee.com</a> and use our services.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-800">Our Commitment</h4>
                <p className="text-blue-700 text-sm mt-1">We do not sell your personal information and only share it with your explicit consent.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'collection',
      title: 'Information We Collect',
      icon: Eye,
      summary: 'Types of data we gather to provide our services',
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Personal Information
              </h4>
              <ul className="space-y-2 text-sm text-purple-700">
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Full name</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Email address</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Phone number</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Resume/CV and job history</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Immigration status (optional)</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>Payment information</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-5 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                <FileText className="w-4 h-4 mr-2" />
                Usage Data
              </h4>
              <ul className="space-y-2 text-sm text-green-700">
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>IP address</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Browser type</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Operating system</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Referring URLs</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Pages visited</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-green-400 rounded-full mr-2"></span>Visit timestamps</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
            <h4 className="font-semibold text-orange-800 mb-2">Cookies and Tracking</h4>
            <p className="text-orange-700 text-sm mb-3">
              We use cookies to enhance your user experience, remember preferences, and analyze site traffic.
            </p>
            <button 
              onClick={() => setShowCookieSettings(!showCookieSettings)}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
            >
              Manage Cookie Settings
            </button>
          </div>
        </div>
      )
    },
    {
      id: 'usage',
      title: 'How We Use Your Information',
      icon: Lock,
      summary: 'Ways we utilize collected data to serve you better',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">We use your information to:</p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Provide and improve our job placement and IT training services',
              'Communicate with you (e.g., email updates, job leads)',
              'Personalize your user experience',
              'Comply with legal obligations',
              'Prevent fraud and misuse of our platform',
              'Analyze usage patterns to enhance our services'
            ].map((item, index) => (
              <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 'sharing',
      title: 'Sharing Your Information',
      icon: Users,
      summary: 'When and with whom we share your data',
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
              <span className="font-semibold text-red-800">We do NOT sell your personal information</span>
            </div>
          </div>
          
          <p className="text-gray-700 mb-4">We may share your information with:</p>
          
          <div className="space-y-3">
            <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <h4 className="font-semibold text-gray-800 mb-2">Trusted Service Providers</h4>
              <p className="text-gray-600 text-sm">CRM platforms, email services, and other tools that help us operate</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <h4 className="font-semibold text-gray-800 mb-2">Employers or Recruiters</h4>
              <p className="text-gray-600 text-sm">Only with your explicit consent for job placement services</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <h4 className="font-semibold text-gray-800 mb-2">Government Authorities</h4>
              <p className="text-gray-600 text-sm">When legally required by applicable laws</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: Shield,
      summary: 'How we protect your information',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            We implement appropriate security measures to protect your information. However, no system can be 100% secure. Please use caution when sharing sensitive information online.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h4 className="font-semibold text-blue-800 text-sm">Encryption</h4>
              <p className="text-blue-600 text-xs mt-1">Data encrypted in transit and at rest</p>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <Lock className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h4 className="font-semibold text-green-800 text-sm">Access Control</h4>
              <p className="text-green-600 text-xs mt-1">Restricted access to authorized personnel</p>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <Eye className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-semibold text-purple-800 text-sm">Monitoring</h4>
              <p className="text-purple-600 text-xs mt-1">Continuous security monitoring</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'rights',
      title: 'Your Rights and Choices',
      icon: CheckCircle,
      summary: 'What you can do with your personal data',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Depending on your location, you may have the right to:
          </p>
          
          <div className="space-y-3">
            {[
              { title: 'Access', desc: 'Request a copy of the personal information we hold about you' },
              { title: 'Correct', desc: 'Update or correct inaccurate personal information' },
              { title: 'Delete', desc: 'Request deletion of your personal information' },
              { title: 'Opt Out', desc: 'Unsubscribe from marketing emails and communications' },
              { title: 'Portability', desc: 'Request your data in a portable format' }
            ].map((right, index) => (
              <div key={index} className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">{right.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{right.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              To make a request, please contact us at{' '}
              <a href="mailto:privacy@techmentee.com" className="font-semibold underline hover:text-blue-600">
                privacy@techmentee.com
              </a>
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'ccpa',
      title: 'California Privacy Rights (CCPA)',
      icon: FileText,
      summary: 'Specific rights for California residents',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            If you are a California resident, the California Consumer Privacy Act (CCPA) provides you with specific rights:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'Right to Know', desc: 'Request categories and specific pieces of personal information we have collected' },
              { title: 'Right to Delete', desc: 'Request deletion of personal information (subject to certain exceptions)' },
              { title: 'Right to Opt Out', desc: 'We do not sell your information, but you can opt out if this changes' },
              { title: 'Right to Non-Discrimination', desc: 'No discrimination for exercising your CCPA rights' }
            ].map((right, index) => (
              <div key={index} className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                <h4 className="font-semibold text-orange-800 mb-2">{right.title}</h4>
                <p className="text-orange-700 text-sm">{right.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-orange-100 border border-orange-300 rounded-lg p-4">
            <p className="text-orange-800 text-sm">
              To exercise CCPA rights, email us at{' '}
              <a href="mailto:privacy@techmentee.com" className="font-semibold underline">
                privacy@techmentee.com
              </a>{' '}
              with "CCPA Request" in the subject line.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'other',
      title: 'Additional Information',
      icon: ExternalLink,
      summary: 'Third-party links, children\'s privacy, and policy updates',
      content: (
        <div className="space-y-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <ExternalLink className="w-4 h-4 mr-2" />
              Third-Party Links
            </h4>
            <p className="text-gray-600 text-sm">
              Our website may contain links to third-party websites. We are not responsible for their content or privacy practices. Please review their privacy policies before sharing any information.
            </p>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Children's Privacy
            </h4>
            <p className="text-gray-600 text-sm">
              Our services are not directed to individuals under the age of 13. We do not knowingly collect personal data from children.
            </p>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              Policy Updates
            </h4>
            <p className="text-gray-600 text-sm">
              We may update this Privacy Policy from time to time. The updated version will be posted on our website with a revised "Effective Date."
            </p>
          </div>
        </div>
      )
    }
  ];

  const filteredSections = sections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Banner pageName="PRIVACY POLICY" breadcrummbs="Home / Privacy Policy" />
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Shield className="w-8 h-8 text-blue-600 mr-3" />
                Privacy Policy
              </h1>
              <p className="text-gray-600 mt-2">Techmentee Inc - Protecting your privacy since day one</p>
            </div>
            <div className="text-right">
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Calendar className="w-4 h-4 mr-2" />
                Last updated: {lastUpdated.toLocaleDateString()}
              </div>
              <div className="text-xs text-gray-400">
                Effective immediately
              </div>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search privacy policy sections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showCookieSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Cookie Settings</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input type="checkbox" defaultChecked disabled className="mr-3" />
                <span className="text-sm">Essential cookies (required)</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" defaultChecked className="mr-3" />
                <span className="text-sm">Analytics cookies</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3" />
                <span className="text-sm">Marketing cookies</span>
              </label>
            </div>
            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => setShowCookieSettings(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowCookieSettings(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="space-y-6">
          {filteredSections.map((section) => {
            const Icon = section.icon;
            const isExpanded = expandedSections.has(section.id);
            const isRead = readingSections.has(section.id);
            
            return (
              <div
                key={section.id}
                className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  isExpanded ? 'border-blue-300' : 'border-gray-200'
                }`}
              >
                <div
                  className={`p-6 cursor-pointer transition-colors ${
                    isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-lg transition-colors ${
                        isExpanded ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          isExpanded ? 'text-blue-600' : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <h2 className={`text-xl font-semibold transition-colors ${
                          isExpanded ? 'text-blue-900' : 'text-gray-900'
                        }`}>
                          {section.title}
                          {isRead && (
                            <CheckCircle className="inline w-5 h-5 text-green-500 ml-2" />
                          )}
                        </h2>
                        <p className="text-gray-600 text-sm mt-1">{section.summary}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 pt-6">
                      {section.content}
                      <div className="mt-6 flex justify-between items-center">
                        <button
                          onClick={() => markAsRead(section.id)}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                            isRead
                              ? 'bg-green-100 text-green-700 cursor-default'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                          disabled={isRead}
                        >
                          {isRead ? 'Section Read ✓' : 'Mark as Read'}
                        </button>
                        <div className="text-xs text-gray-400">
                          Section {sections.findIndex(s => s.id === section.id) + 1} of {sections.length}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl text-white p-8">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-2">Questions About Your Privacy?</h2>
            <p className="mb-6 opacity-90">
              We're here to help. Contact us with any questions about this Privacy Policy or your data.
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-center">
                <Mail className="w-4 h-4 mr-2" />
                <a href="mailto:privacy@techmentee.com" className="font-semibold hover:underline">
                  privacy@techmentee.com
                </a>
              </div>
              <div className="flex items-center justify-center">
                <ExternalLink className="w-4 h-4 mr-2" />
                <a href="https://learn.techmentee.com" className="font-semibold hover:underline">
                  learn.techmentee.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mt-8 text-center">
          <div className="text-sm text-gray-600 mb-2">
            Reading Progress: {readingSections.size} of {sections.length} sections read
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(readingSections.size / sections.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;